import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
	CreateSaleRequest,
	ManagementBusinessEntityControllerService,
	ManagementBusinessEntityResponse,
	ManagementSaleControllerService,
	ManagementSaleResponse,
} from 'api-contract-notices';
import { UtilsService } from '../../../../../shared/services/utils.service';
import { debounceTime, distinctUntilChanged, startWith, Subject, take, takeUntil } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BusinessEntityComponent } from '../../../management/business-entity/business-entity';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UploadManagementService } from '../../../../../shared/services/upload-management.service';
import { MatChipEvent, MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { SharedService } from '../../../../../shared/services/shared.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'app-sales-form',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		MatFormFieldModule,
		MatAutocompleteModule,
		MatInputModule,
		TranslateModule,
		MatIconModule,
		MatButtonModule,
		MatTooltipModule,
		MatSelectModule,
		MatDatepickerModule,
		MatProgressSpinnerModule,
		MatChipsModule,
		MatCheckboxModule,
	],
	templateUrl: './sales-form.component.html',
	styleUrl: './sales-form.component.scss',
})
export class SalesFormComponent implements OnInit, OnChanges, OnDestroy {
	private onDestroy: Subject<boolean> = new Subject();

	@Input() itemForEdit: ManagementSaleResponse | undefined;
	@Output() actionRequested: EventEmitter<any> = new EventEmitter();

	salesFormGroup: FormGroup = new FormGroup({});

	mode: 'NEW' | 'EDIT' = 'NEW';
	private readonly DEBOUNCE_TIME = 450;

	apiLoading = {
		sales: false,
	};

	filteredBusinessEntities: ManagementBusinessEntityResponse[] = [];
	@ViewChild('trusteeInput', { static: false }) trusteeInput!: ElementRef<HTMLInputElement>;
	selectedTrustees: ManagementBusinessEntityResponse[] = [];
	@ViewChild('debtorInput', { static: false }) debtorInput!: ElementRef<HTMLInputElement>;
	selectedDebtors: ManagementBusinessEntityResponse[] = [];

	allLocations: string[] = [];
	filteredLocations: string[] = [];
	@ViewChild('placesOfSaleInput', { static: false }) placesOfSaleInput!: ElementRef<HTMLInputElement>;
	selectedPlacesOfSale: string[] = [];
	@ViewChild('locationsInput', { static: false }) locationsInput!: ElementRef<HTMLInputElement>;
	selectedLocations: string[] = [];

	types: string[] = ['REAL_ESTATE', 'MOVABLE_PROPERTY', 'REAL_MOVABLE', 'BUILDABLE_LAND', 'LEASE_RENTAL', 'AMENDMENT', 'CANCELLATION'];
	constructor(
		private fb: FormBuilder,
		public utils: UtilsService,
		private apiManagementBusinessEntity: ManagementBusinessEntityControllerService,
		private dialog: MatDialog,
		private uploadManagement: UploadManagementService,
		private apiManagementSale: ManagementSaleControllerService,
		private sharedService: SharedService,
		private translate: TranslateService
	) {
		this.sharedService.requestActionInComponent.pipe(takeUntilDestroyed()).subscribe({
			next: response => {
				if (response.componentName === 'SalesFormComponent') {
					switch (response.action) {
						case 'RESET_FORM': {
							this.resetForm();
							break;
						}
					}
				}
			},
		});

		this.formInitializer();
		this.getLocations();
	}

	ngOnInit(): void {
		this.formControlWatchers();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['itemForEdit'].currentValue) {
			this.salesFormGroup.reset();
			this.mode = 'EDIT';
			this.populateForm();
		}
	}

	ngOnDestroy(): void {
		this.onDestroy.next(true);
	}

	/** Form methods */
	private formInitializer() {
		this.salesFormGroup = this.fb.group({
			trustees: [''],
			debtors: [''],
			placesOfSale: [],
			locations: [''],
			fileUrl: [],
			publicationUrl: [, [Validators.pattern(this.utils.VALIDATION_PATTERNS.get('URL') || '')]],
			type: [this.types[0], Validators.required],
			publicationDate: [new Date(), Validators.required],
			saleDate: [, Validators.required],
			text: [''],
			eSale: [false],
		});
	}

	private formControlWatchers() {
		if (this.salesFormGroup.controls['trustees']) {
			this.salesFormGroup.controls['trustees'].valueChanges
				.pipe(startWith(''), debounceTime(this.DEBOUNCE_TIME), distinctUntilChanged(), takeUntil(this.onDestroy))
				.subscribe(searchTerm => {
					this.itemAction('SEARCH', 'TRUSTEE', searchTerm);
				});
		}
		if (this.salesFormGroup.controls['debtors']) {
			this.salesFormGroup.controls['debtors'].valueChanges
				.pipe(startWith(''), debounceTime(this.DEBOUNCE_TIME), distinctUntilChanged(), takeUntil(this.onDestroy))
				.subscribe(searchTerm => {
					this.itemAction('SEARCH', 'DEBTOR', searchTerm);
				});
		}
		if (this.salesFormGroup.controls['placesOfSale']) {
			this.salesFormGroup.controls['placesOfSale'].valueChanges
				.pipe(startWith(''), debounceTime(this.DEBOUNCE_TIME), distinctUntilChanged(), takeUntil(this.onDestroy))
				.subscribe(searchTerm => {
					this.itemAction('SEARCH', 'PLACE_OF_SALE', searchTerm);
				});
		}
		if (this.salesFormGroup.controls['locations']) {
			this.salesFormGroup.controls['locations'].valueChanges
				.pipe(startWith(''), debounceTime(this.DEBOUNCE_TIME), distinctUntilChanged(), takeUntil(this.onDestroy))
				.subscribe(searchTerm => {
					this.itemAction('SEARCH', 'LOCATION', searchTerm);
				});
		}
	}

	checkFormValidity(): boolean {
		let validity = false;

		validity =
			(this.selectedTrustees.length || this.selectedDebtors.length) && this.selectedLocations.length &&
			(this.salesFormGroup.controls['fileUrl'].value || this.salesFormGroup.controls['text'].value || this.salesFormGroup.controls['publicationUrl'].value);

		return validity;
	}

	private populateForm() {
		if (this.itemForEdit) {
			this.salesFormGroup.patchValue(this.itemForEdit);

			this.salesFormGroup.controls['trustees'].setValue(null);
			this.selectedTrustees = this.itemForEdit.trustees || [];

			this.salesFormGroup.controls['debtors'].setValue(null);
			this.selectedDebtors = this.itemForEdit.debtors || [];

			this.salesFormGroup.controls['placesOfSale'].setValue(null);
			this.selectedPlacesOfSale = this.itemForEdit.placesOfSale || [];

			this.salesFormGroup.controls['locations'].setValue(null);
			this.selectedLocations = this.itemForEdit.locations || [];
		}
	}

	resetForm(formDirective?: FormGroupDirective) {
		this.salesFormGroup.reset();

		this.filteredBusinessEntities = [];
		this.selectedTrustees = [];
		this.selectedDebtors = [];
		this.selectedLocations = [];
		this.selectedPlacesOfSale = [];

		setTimeout(() => {
			this.salesFormGroup.controls['publicationDate'].setValue(new Date());
			this.salesFormGroup.controls['type'].setValue(this.types[0]);
		}, 100);

		if (formDirective) {
			formDirective.resetForm();
		}

		this.mode = 'NEW';
	}
	/** (END)Form methods */

	itemAction(
		action: 'SEARCH' | 'CREATE' | 'SELECT' | 'ADD' | 'REMOVE',
		type: 'TRUSTEE' | 'DEBTOR' | 'PLACE_OF_SALE' | 'LOCATION',
		data: string | MatChipInputEvent | MatAutocompleteSelectedEvent | ManagementBusinessEntityResponse
	) {
		switch (type) {
			case 'TRUSTEE': {
				if (action === 'SEARCH') {
					this.searchBusinessEntity(data as string);
				} else if (action === 'CREATE') {
					this.openBusinessEntityManagementDialog();
				} else if (action === 'SELECT') {
					this.selectedTrustees.push((data as MatAutocompleteSelectedEvent).option.value);
					this.salesFormGroup.controls['trustees'].setValue(null);
					this.trusteeInput.nativeElement.value = '';
				} else if (action === 'REMOVE') {
					this.selectedTrustees = this.selectedTrustees.filter(st => st.id !== (data as ManagementBusinessEntityResponse).id);
				}
				break;
			}
			case 'DEBTOR': {
				if (action === 'SEARCH') {
					this.searchBusinessEntity(data as string);
				} else if (action === 'CREATE') {
					this.openBusinessEntityManagementDialog();
				} else if (action === 'SELECT') {
					this.selectedDebtors.push((data as MatAutocompleteSelectedEvent).option.value);
					this.salesFormGroup.controls['debtors'].setValue(null);
					this.debtorInput.nativeElement.value = '';
				} else if (action === 'REMOVE') {
					this.selectedDebtors = this.selectedDebtors.filter(st => st.id !== (data as ManagementBusinessEntityResponse).id);
				}
				break;
			}
			case 'PLACE_OF_SALE': {
				if (action === 'SEARCH') {
					this.searchLocations(data as string);
				} else if (action === 'SELECT' || action === 'ADD') {
					if ((data as MatChipInputEvent).value || (data as MatAutocompleteSelectedEvent).option.value) {
						this.selectedPlacesOfSale.push(action === 'ADD' ? (data as MatChipInputEvent).value : (data as MatAutocompleteSelectedEvent).option.value);
						this.salesFormGroup.controls['placesOfSale'].setValue(null);
						this.placesOfSaleInput.nativeElement.value = '';
						this.selectedLocations.push(action === 'ADD' ? (data as MatChipInputEvent).value : (data as MatAutocompleteSelectedEvent).option.value);
						this.salesFormGroup.controls['locations'].setValue(null);
						this.locationsInput.nativeElement.value = '';
					}
				} else if (action === 'REMOVE') {
					this.selectedPlacesOfSale = this.selectedPlacesOfSale.filter(sps => sps !== data);
				}
				break;
			}
			case 'LOCATION': {
				if (action === 'SEARCH') {
					this.searchLocations(data as string);
				} else if (action === 'SELECT' || action === 'ADD') {
					this.selectedLocations.push(action === 'ADD' ? (data as MatChipInputEvent).value : (data as MatAutocompleteSelectedEvent).option.value);
					this.salesFormGroup.controls['locations'].setValue(null);
					this.locationsInput.nativeElement.value = '';
				} else if (action === 'REMOVE') {
					this.selectedLocations = this.selectedLocations.filter(sps => sps !== data);
				}
				break;
			}
		}
	}

	//** Business entity methods */
	private searchBusinessEntity(searchTerm: string) {
		if (searchTerm && searchTerm.length > 0) {
			this.apiLoading.sales = true;

			this.apiManagementBusinessEntity
				.getBusinessEntitiesPageable({ businessEntity: searchTerm }, 0, 100)
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.filteredBusinessEntities = response.content || [];
						this.apiLoading.sales = false;
					},
					error: () => {
						this.apiLoading.sales = false;
					},
				});
		} else {
			this.filteredBusinessEntities = [];
		}
	}
	displayFnBusinessEntity(item: ManagementBusinessEntityResponse): string {
		return item && item.id ? this.utils.getParameterByLanguageScript(item, 'name', true) : '';
	}

	private openBusinessEntityManagementDialog() {
		const dialogRef = this.dialog.open(BusinessEntityComponent, {
			width: '600px',
			data: {
				mode: 'NEW',
				type: 'BUSINESS_ENTITY',
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(result => {
				this.filteredBusinessEntities = [];
			});
	}
	//** (END)Business entity methods */

	//** Locations methods */
	private getLocations() {
		this.apiManagementSale
			.findAllLocationsAndPlacesInSales()
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.allLocations = response;
				},
			});
	}

	private searchLocations(searchTerm: string) {
		if (searchTerm && searchTerm.length > 0) {
			this.filteredLocations = this.allLocations.filter(l => l.toLowerCase().includes(searchTerm.toLowerCase()));
		} else {
			this.filteredLocations = [];
		}
	}
	//** (END)Locations methods */

	//** Image methods */
	getAcceptedFileFormats() {
		return ['image/*', 'application/pdf'];
	}

	selectFilesForUpload(event: any) {
		this.uploadManagement.generatePresignedUrl(event.target.files[0], 'SALE', 'CONTRACT_NOTICES');
		this.uploadManagement.linkGenerated.pipe(take(1)).subscribe(response => {
			this.salesFormGroup.controls['fileUrl'].setValue(response);
		});
	}
	//** (END)Image methods */

	submitSalesForm(formDirective: FormGroupDirective, event: Event) {
		event.stopPropagation();
		event.preventDefault();
		this.apiLoading.sales = true;

		const saleRequest: CreateSaleRequest = {
			trusteesIds: this.selectedTrustees.map(st => st.id),
			debtorsIds: this.selectedDebtors.map(sd => sd.id),
			locations: this.selectedLocations,
			placesOfSale: this.selectedPlacesOfSale,
			publicationUrl: this.salesFormGroup.controls['publicationUrl'].value,
			fileUrl: this.salesFormGroup.controls['fileUrl'].value,
			type: this.salesFormGroup.controls['type'].value,
			publicationDate: new Date(this.utils.setDateToDay(new Date(this.salesFormGroup.controls['publicationDate'].value), 'CURRENT_TIME')),
			saleDate: this.salesFormGroup.controls['saleDate'].value,
			text: this.salesFormGroup.controls['text'].value,
			eSale: this.salesFormGroup.controls['eSale'].value,
		};

		if (this.mode === 'NEW') {
			this.apiManagementSale
				.createSale(saleRequest)
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
							type: 'SUCCESS',
						});

						if (saleRequest.fileUrl) {
							this.uploadManagement.uploadFile(response);
						}

						this.resetForm(formDirective);
						this.actionRequested.emit({ action: 'LOAD' });
						this.apiLoading.sales = false;
						this.getLocations();
					},
					error: err => {
						this.sharedService.showNotification.next({
							message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
							type: 'ERROR',
						});
						this.apiLoading.sales = false;
					},
				});
		} else if (this.mode === 'EDIT' && this.itemForEdit) {
			this.apiManagementSale
				.updateSale(saleRequest, this.itemForEdit.id)
				.pipe(take(1))
				.subscribe({
					next: () => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
							type: 'SUCCESS',
						});

						if (this.itemForEdit && !this.utils.compareSuppressNulls(this.itemForEdit.fileUrl, saleRequest.fileUrl)) {
							this.uploadManagement.uploadFile(this.itemForEdit);
						}

						this.resetForm(formDirective);
						this.actionRequested.emit({ action: 'LOAD' });
						this.apiLoading.sales = false;
						this.mode = 'NEW';
						this.getLocations();
					},
					error: err => {
						this.sharedService.showNotification.next({
							message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
							type: 'ERROR',
						});
						this.apiLoading.sales = false;
					},
				});
		}
	}
}
